export interface DripInput {
  initialInvestment: number;
  monthlyContribution: number;
  /** Angenommener Startkurs je Anteil, nur zur Umrechnung Investition -> Stückzahl. */
  initialPricePerShare: number;
  /** Angenommene, konstante jährliche Kurssteigerung als Dezimalzahl. */
  annualPriceGrowthRate: number;
  /** Angenommene Dividendenrendite auf den aktuellen Kurs zu Beginn, als Dezimalzahl. */
  initialDividendYield: number;
  /** Angenommenes jährliches Dividendenwachstum je Anteil als Dezimalzahl. */
  annualDividendGrowthRate: number;
  years: number;
}

export interface DripYearPoint {
  year: number;
  totalContributions: number;
  valueWithReinvest: number;
  valueWithoutReinvest: number;
}

export interface DripResult {
  points: DripYearPoint[];
  finalValueWithReinvest: number;
  finalValueWithoutReinvest: number;
  totalContributions: number;
  /** Kumulierte Dividenden, die über die Laufzeit reinvestiert wurden (nominal, nicht selbst verzinst). */
  totalDividendsReinvested: number;
}

/**
 * Dividenden-Reinvestitions-Rechner (backlog.md -> Weiterer Nutzwert-Katalog,
 * "Dividenden-Reinvestitions-Rechner (Zinseszins speziell für reinvestierte Dividenden)
 * als Ergänzung im Dividenden-Tracker") - rein hypothetische Modellrechnung mit
 * angenommenen konstanten Raten (Kurswachstum, Dividendenrendite, Dividendenwachstum),
 * keine Prognose für eine konkrete Aktie. Anders als computeCompoundInterest() rechnet
 * dieser Rechner mit Stückzahlen (Anteile), weil das Kernkonzept - Dividenden kaufen
 * weitere Anteile, die selbst wieder Dividenden abwerfen - sich nur so korrekt abbilden
 * lässt. Rechnet ZWEI parallele Szenarien mit identischen Einzahlungen, um den Effekt der
 * Reinvestition sichtbar zu machen: "mit Reinvestition" (Dividenden kaufen zusätzliche
 * Anteile) vs. "ohne Reinvestition" (Dividenden würden ausgezahlt statt investiert, nur
 * der reine Kurswert der Position). Dividenden werden vereinfachend einmal jährlich
 * ausgeschüttet (statt quartalsweise) - für eine reine Illustration ausreichend genau.
 * Reine Funktion, kein I/O.
 */
export function computeDripProjection(input: DripInput): DripResult | undefined {
  const {
    initialInvestment,
    monthlyContribution,
    initialPricePerShare,
    annualPriceGrowthRate,
    initialDividendYield,
    annualDividendGrowthRate,
    years,
  } = input;

  if (
    !Number.isFinite(initialInvestment) ||
    !Number.isFinite(monthlyContribution) ||
    !Number.isFinite(annualPriceGrowthRate) ||
    !Number.isFinite(initialDividendYield) ||
    !Number.isFinite(annualDividendGrowthRate) ||
    initialInvestment < 0 ||
    monthlyContribution < 0 ||
    !Number.isFinite(initialPricePerShare) ||
    initialPricePerShare <= 0 ||
    initialDividendYield <= 0 ||
    initialDividendYield > 0.3 ||
    !Number.isInteger(years) ||
    years < 1 ||
    years > 60
  ) {
    return undefined;
  }

  const monthlyPriceGrowth = Math.pow(1 + annualPriceGrowthRate, 1 / 12) - 1;
  let price = initialPricePerShare;
  let dividendPerShare = initialDividendYield * initialPricePerShare;
  let sharesWithReinvest = initialInvestment / initialPricePerShare;
  let sharesWithoutReinvest = initialInvestment / initialPricePerShare;
  let totalContributions = initialInvestment;
  let totalDividendsReinvested = 0;

  const points: DripYearPoint[] = [
    { year: 0, totalContributions, valueWithReinvest: initialInvestment, valueWithoutReinvest: initialInvestment },
  ];

  for (let month = 1; month <= years * 12; month++) {
    price *= 1 + monthlyPriceGrowth;
    if (monthlyContribution > 0) {
      const newShares = monthlyContribution / price;
      sharesWithReinvest += newShares;
      sharesWithoutReinvest += newShares;
      totalContributions += monthlyContribution;
    }

    if (month % 12 === 0) {
      const dividendCash = sharesWithReinvest * dividendPerShare;
      sharesWithReinvest += dividendCash / price;
      totalDividendsReinvested += dividendCash;
      dividendPerShare *= 1 + annualDividendGrowthRate;

      points.push({
        year: month / 12,
        totalContributions,
        valueWithReinvest: sharesWithReinvest * price,
        valueWithoutReinvest: sharesWithoutReinvest * price,
      });
    }
  }

  return {
    points,
    finalValueWithReinvest: sharesWithReinvest * price,
    finalValueWithoutReinvest: sharesWithoutReinvest * price,
    totalContributions,
    totalDividendsReinvested,
  };
}
